import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'

import { blogPostPageModuleQuery } from '@data/sanity/queries/page'
import {
  SanityBlogPostPage,
  SanityBlogPostWithoutBody,
} from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getAllLocales, getLocaleLanguage, Locale } from '@lib/language'
import { useBlogCanonicalUrl } from '@lib/metadata'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  CustomStaticPropsContext,
  PageType,
  StaticPathWithSlug,
} from '@lib/routes'
import {
  getAllBlogSettings,
  getBlogLocale,
  getBlogPostPage,
  getBlogPosts,
} from '@lib/sanity/blog'
import { getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'
import { getBlogPostSchema } from '@lib/schema'

import Layout from '@components/layout'
import Modules from '@modules/modules'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogPostPageProps = BasePageProps & {
  blogLocale: Locale
  slug: string
  page: SanityBlogPostPage
  site: SanitySiteFragment
}

const BlogPostPage = ({
  draftMode,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogPostPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl(
    PageType.BLOG_POST,
    page.slug,
    site.blogSettings,
  )

  const blogPostSchema = getBlogPostSchema(page, site)

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      blogPostSchema={blogPostSchema}
      draftMode={draftMode}
      className="bg-gray text-black"
    >
      {!draftMode && <Modules posts={page.blogPosts} />}
      {draftMode && (
        <PreviewModules
          posts={page.blogPosts}
          query={blogPostPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
            blogPostSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface BlogPostPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogPostPageParams>,
): Promise<GetStaticPropsResult<BlogPostPageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, locale)
  const slug = context.params?.slug ?? ''
  const blogPostPage = await getBlogPostPage(
    sanityClient,
    locale,
    blogLocale,
    slug,
  )

  if (!blogPostPage.page) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  // TODO: Do all this in a single query.
  let blogPosts: Array<SanityBlogPostWithoutBody> = []
  const blogPostLimit = 5

  const relatedBlogPosts = await getBlogPosts(
    sanityClient,
    locale,
    0,
    blogPostLimit,
    blogPostPage.page.categories?.map((category) => category.slug) ?? [],
    [blogPostPage.page.id],
  )

  if (relatedBlogPosts) {
    blogPosts = relatedBlogPosts
  }

  if (blogPosts.length < blogPostLimit) {
    const additionalBlogPosts = await getBlogPosts(
      sanityClient,
      locale,
      0,
      blogPostLimit - blogPosts.length,
      [],
      [blogPostPage.page.id, ...(relatedBlogPosts?.map(({ id }) => id) ?? [])],
    )

    if (additionalBlogPosts) {
      blogPosts = [...blogPosts, ...additionalBlogPosts]
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      slug,
      page: {
        ...blogPostPage.page,
        blogPosts,
      },
      site: blogPostPage.site,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogPostPageParams>
> {
  const sanityClient = getSanityClient()
  const blogPosts = await getAllSlugs(sanityClient, 'blogPost')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogPosts.forEach((blogPost) => {
    const locale = blogPost.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({ params: { slug: blogPost.slug }, locale })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({ params: { slug: blogPost.slug }, locale: childLocale })
      }
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogPostPage
